import {
  MeshBuilder,
  StandardMaterial,
  Color3,
  type Scene,
  type AbstractMesh,
  type ShadowGenerator
} from '@babylonjs/core';

export class MonsterMesh {
  public rootMesh: AbstractMesh;
  public bodyMesh: AbstractMesh;
  private animTimer: number = 0;

  constructor(scene: Scene, id: string, shadowGenerator?: ShadowGenerator) {
    // Root container
    this.rootMesh = MeshBuilder.CreateBox(`monster_root_${id}`, { size: 0.1 }, scene);
    this.rootMesh.isVisible = false;

    // Body
    this.bodyMesh = MeshBuilder.CreateSphere(
      `monster_body_${id}`,
      { diameterX: 0.8, diameterY: 0.7, diameterZ: 0.9, segments: 16 },
      scene
    );
    this.bodyMesh.parent = this.rootMesh;
    this.bodyMesh.position.y = 0.35;

    const bodyMat = new StandardMaterial(`monster_mat_${id}`, scene);
    bodyMat.diffuseColor = new Color3(0.92, 0.65, 0.38); // warm caramel fur
    bodyMat.specularColor = new Color3(0.1, 0.1, 0.1);
    this.bodyMesh.material = bodyMat;

    // Ears / Horns
    const hornMat = new StandardMaterial(`monster_horn_mat_${id}`, scene);
    hornMat.diffuseColor = new Color3(0.95, 0.9, 0.75); // ivory

    const hornLeft = MeshBuilder.CreateCylinder(
      `monster_horn_L_${id}`,
      { height: 0.5, diameterTop: 0.05, diameterBottom: 0.15 },
      scene
    );
    hornLeft.parent = this.bodyMesh;
    hornLeft.position.set(-0.2, 0.45, 0.15);
    hornLeft.rotation.z = -0.2;
    hornLeft.material = hornMat;

    const hornRight = MeshBuilder.CreateCylinder(
      `monster_horn_R_${id}`,
      { height: 0.5, diameterTop: 0.05, diameterBottom: 0.15 },
      scene
    );
    hornRight.parent = this.bodyMesh;
    hornRight.position.set(0.2, 0.45, 0.15);
    hornRight.rotation.z = 0.2;
    hornRight.material = hornMat;

    // Eyes
    const eyeMat = new StandardMaterial(`monster_eye_mat_${id}`, scene);
    eyeMat.diffuseColor = new Color3(0.08, 0.08, 0.1);

    const eyeLeft = MeshBuilder.CreateSphere(`monster_eye_L_${id}`, { diameter: 0.12 }, scene);
    eyeLeft.parent = this.bodyMesh;
    eyeLeft.position.set(-0.16, 0.1, 0.4);
    eyeLeft.material = eyeMat;

    const eyeRight = MeshBuilder.CreateSphere(`monster_eye_R_${id}`, { diameter: 0.12 }, scene);
    eyeRight.parent = this.bodyMesh;
    eyeRight.position.set(0.16, 0.1, 0.4);
    eyeRight.material = eyeMat;

    if (shadowGenerator) {
      shadowGenerator.addShadowCaster(this.bodyMesh);
    }
  }

  public setPosition(x: number, y: number, z: number): void {
    this.rootMesh.position.set(x, y, z);
  }

  public update(dt: number, isMoving: boolean = false): void {
    this.animTimer += dt * (isMoving ? 8.0 : 3.0);
    const hop = Math.abs(Math.sin(this.animTimer)) * (isMoving ? 0.3 : 0.06);
    this.bodyMesh.position.y = 0.35 + hop;

    // Gentle breathing scale
    const breath = 1.0 + Math.sin(this.animTimer * 0.5) * 0.03;
    this.bodyMesh.scaling.set(breath, 1.0 / breath, breath);
  }
}
